import { Alert, Breadcrumbs, Loader } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { ApplicationState } from '@xver/store';
import DialogBox from '@xver/components/Atoms/DialogBox/DialogBox';
import useBreadcrumbItems from '@xver/hooks/useBreadcrumbItems';
import usePageHeading from '@xver/hooks/usePageHeading';
import { useMeQuery } from '@xver/hooks/useMeQuery';

import BillingAddressSection from './components/BillingAddressSection';
import BillingHistoryTable from './components/BillingHistoryTable';
import CreditBreakdownSection from './components/CreditBreakdownSection';
import CreditCard from './components/CreditCard';
import SpendByProductSection from './components/SpendByProductSection';
import {
    useBillingHistoryQuery,
    useBillingQuery,
    useBillingUnsetQuery,
    useCreditSummaryQuery,
    useUpdateBillingMutation,
} from './hooks/useBillingQueries';

const PAGE_SIZE = 10;

export default function BillingsPage() {
    const { t } = useTranslation('billings');
    const headingRef = usePageHeading(t('page.title'));
    const cachedUser = useSelector((state: ApplicationState) => state.auth.user);
    const { data: freshUser } = useMeQuery();
    const user = freshUser ?? cachedUser;

    const [page, setPage] = useState(1);

    const billingQuery = useBillingQuery();
    const unsetQuery = useBillingUnsetQuery();
    const historyQuery = useBillingHistoryQuery(page, PAGE_SIZE);
    const creditSummaryQuery = useCreditSummaryQuery();
    const updateMutation = useUpdateBillingMutation();

    const billingAddressComplete = unsetQuery.data?.status === false;

    const breadcrumbItems = useBreadcrumbItems([
        { title: t('breadcrumb.account'), href: '#' },
        { title: t('breadcrumb.billings') },
    ]);

    return (
        <div className="w-full min-h-full flex flex-col justify-start items-center gap-8">
            <div className="w-full flex flex-col justify-start items-start">
                <Breadcrumbs separatorMargin="xs" className="mb-6">
                    {breadcrumbItems}
                </Breadcrumbs>
                <div className="w-full flex flex-col gap-1">
                    <h1 ref={headingRef} tabIndex={-1} className="text-3xl font-bold">
                        {t('page.title')}
                    </h1>
                    <p className="w-auto max-w-135 text-base text-fg/68">{t('page.description')}</p>
                </div>
            </div>
            <div className="w-full flex flex-col gap-4">
                {unsetQuery.data?.status === true && (
                    <Alert color="yellow" icon={<IconAlertTriangle size={18} />} variant="light">
                        {t('warning.incompleteAddress')}
                    </Alert>
                )}
                {user ? (
                    <CreditCard credit={user.credit} />
                ) : (
                    <div className="w-full flex items-center justify-center py-8">
                        <Loader size="sm" color="var(--fg)" />
                    </div>
                )}
                <CreditBreakdownSection
                    summary={creditSummaryQuery.data}
                    isLoading={creditSummaryQuery.isPending}
                    isError={creditSummaryQuery.isError}
                />
                <SpendByProductSection
                    summary={creditSummaryQuery.data}
                    isLoading={creditSummaryQuery.isPending}
                    isError={creditSummaryQuery.isError}
                />
                <BillingAddressSection
                    billing={billingQuery.data}
                    onSubmit={(payload) => updateMutation.mutateAsync(payload)}
                    isSubmitting={updateMutation.isPending}
                />
                <DialogBox
                    label={t('history.title')}
                    description={t('history.description')}
                    classNames="p-4"
                >
                    <BillingHistoryTable
                        response={historyQuery.data}
                        isLoading={historyQuery.isPending}
                        page={page}
                        onPageChange={setPage}
                        billingAddressComplete={billingAddressComplete}
                    />
                </DialogBox>
            </div>
        </div>
    );
}
